import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useActiveContextReady } from './useActiveContextReady';
import { config } from '../config';

/** One collector that currently vouches for an address. */
export interface AddressSighting {
  /** `snmp`, `eapi`, `ssh`, `lldp`, `manual`, … */
  source: string;
  interfaceName: string | null;
  vrf: string | null;
  firstSeenAt: string;
  lastSeenAt: string;
}

export interface DeviceAddress {
  ipAddress: string;
  family: 'ipv4' | 'ipv6';
  /** The address the device is identified and polled by. */
  isPrimary: boolean;
  /** Why the address is not reachable from the network (`loopback`, `link-local`, …), or null. */
  internal: string | null;
  firstSeenAt: string;
  lastSeenAt: string;
  sightings: AddressSighting[];
}

interface DeviceAddressesResponse {
  addresses: DeviceAddress[];
}

/** Every address a monitored device is known by, and which collectors saw each. */
export function useDeviceAddresses(deviceId: string | undefined) {
  const contextReady = useActiveContextReady();
  return useQuery<DeviceAddress[]>({
    queryKey: ['deviceAddresses', deviceId],
    queryFn: async () => {
      if (!deviceId) throw new Error('Device ID is required');
      const response = await axios.get<DeviceAddressesResponse>(
        `${config.backendApi}/api/devices/${deviceId}/addresses`,
        { withCredentials: true },
      );
      return response.data.addresses;
    },
    enabled: !!deviceId && contextReady,
  });
}
